import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Send, MessageCircle } from 'lucide-react';

export default function Registration() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    note: '',
  });

  const [isLoading, setIsLoading] = useState(false);

  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.email) {
      toast.error('Email je povinný');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('https://formspree.io/f/xldodrlw', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name || 'Neuvedeno',
          email: formData.email,
          message: formData.note || 'Žádná zpráva',
        }),
      });

      if (response.ok) {
        toast.success('Děkuji! Vaše zpráva byla přijata.');
        setFormData({ name: '', email: '', note: '' });
      } else {
        toast.error('Chyba při odesílání. Zkuste prosím později.');
      }
    } catch (error) {
      toast.error('Chyba při odesílání. Zkuste prosím později.');
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section
      className="py-24 md:py-36 px-4 relative overflow-hidden"
      id="registration-section"
      ref={ref}
      style={{
        backgroundColor: `rgb(71, 107, 252)`,
      }}
    >
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-1/3 left-10 w-72 h-72 rounded-full bg-white/5 blur-3xl"
          animate={{ scale: [1, 1.3, 1], opacity: [0.1, 0.15, 0.1] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-1/4 right-10 w-96 h-96 rounded-full bg-white/5 blur-3xl"
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.08, 0.12, 0.08] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 3 }}
        />
      </div>

      <div className="max-w-2xl mx-auto relative z-10">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        >
          <motion.div
            className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/10 mb-6"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <MessageCircle className="w-8 h-8 text-white" />
          </motion.div>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-white">
            Napište nám
          </h2>
          <p className="text-white/80 text-lg max-w-xl mx-auto">
            Máte-li připomínky nebo dotazy, prosím, napište nám.
          </p>
        </motion.div>

        <motion.form
          onSubmit={handleSubmit}
          className="space-y-6 bg-white p-8 md:p-10 rounded-3xl shadow-2xl"
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="space-y-2">
            <label htmlFor="name" className="block text-sm font-medium text-foreground">
              Jméno <span className="text-foreground/50">(nepovinné)</span>
            </label>
            <Input
              id="name"
              type="text"
              placeholder="Vaše jméno"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="bg-muted/30 border-gray-200 focus:border-primary text-foreground h-12 rounded-xl transition-all duration-300"
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="email" className="block text-sm font-medium text-foreground">
              Email <span className="text-destructive">*</span>
            </label>
            <Input
              id="email"
              type="email"
              placeholder="váš@email.com"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              className="bg-muted/30 border-gray-200 focus:border-primary text-foreground h-12 rounded-xl transition-all duration-300"
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="note" className="block text-sm font-medium text-foreground">
              Zpráva
            </label>
            <Textarea
              id="note"
              placeholder="Napište nám, co máte na srdci..."
              value={formData.note}
              onChange={(e) => setFormData({ ...formData, note: e.target.value })}
              className="bg-muted/30 border-gray-200 focus:border-primary text-foreground resize-none rounded-xl min-h-[120px] transition-all duration-300"
              rows={4}
            />
          </div>

          <Button
            type="submit"
            disabled={isLoading}
            className="btn-shimmer w-full bg-primary hover:bg-primary/90 text-white font-semibold py-6 text-base rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50"
          >
            {isLoading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Odesílám...
              </span>
            ) : (
              <span className="flex items-center justify-center">
                <Send className="mr-2 h-5 w-5" />
                Odeslat zprávu
              </span>
            )}
          </Button>

          <p className="text-xs text-foreground/50 text-center pt-2">
            Vaše údaje budou odeslány na info@swimmie.cz.
          </p>
        </motion.form>
      </div>
    </section>
  );
}
